import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Spinner, Dropdown, Button } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import guestRequestApi from '../services/guestRequestApi';
import GuestRequestModal from './GuestRequestModal';
import { formatDate, formatRelativeTime } from '../utils/dateFormatter';
import {
  GR_TYPE_LABELS as TYPE_LABELS,
  GR_STATUS_LABELS as STATUS_LABELS,
  GR_STATUS_LABELS_OWNER as OWNER_STATUS_LABELS,
  GR_PRIORITY_LABELS as PRIORITY_LABELS
} from '../config/guestRequestLabels';
import '../styles/BookingGuestRequestsSection.css';
import '../styles/GuestRequestsHistory.css';

// Guest Requests Hub V1 — Phase 5.
// Mounts on GuestDetailsPage. Aggregates a guest's requests across all bookings,
// groups by booking, and provides a guest-centric creation flow that picks a
// target booking before delegating to the same GuestRequestModal used on
// BookingDetails. Read-only rows here; status mutations stay on BookingDetails.
//
// Labels imported from config/guestRequestLabels (V1.1 centralization).
// Local STATUS_ORDER / PRIORITY_ORDER stay here — used for in-group sort.

const STATUS_ORDER = { 'new': 0, 'in-progress': 1, 'completed': 2, 'cancelled': 3 };
const PRIORITY_ORDER = { urgent: 0, normal: 1 };

// Modal hard-blocks cancelled bookings (server-side 409 + client-side guard).
// Filter the booking selector to anything else — completed bookings still allowed
// because ops occasionally records post-stay requests (e.g. lost-and-found).
const isBookingSelectable = (b) => b && b.status !== 'cancelled';

const GuestRequestsHistory = ({ guest }) => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // TurnKey owners observe service delivery — no internal task refs/statuses,
  // no assignee, no request creation; statuses shown in owner-facing wording.
  const isTurnKeyOwner = user?.role === 'turnKeyOwner';
  const statusLabels = isTurnKeyOwner ? OWNER_STATUS_LABELS : STATUS_LABELS;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const guestId = guest?._id;
  const guestBookings = useMemo(() => guest?.bookings || [], [guest]);

  const selectableBookings = useMemo(
    () => guestBookings.filter(isBookingSelectable),
    [guestBookings]
  );

  const fetchRequests = useCallback(async () => {
    if (!guestId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await guestRequestApi.list({ guestId });
      if (result?.success) {
        setRequests(result.requests || []);
      } else {
        setError(result?.error || 'حدث خطأ في جلب الطلبات');
      }
    } catch (err) {
      const backendMsg = err.response?.data?.error || err.response?.data?.message;
      setError(backendMsg || 'حدث خطأ في جلب الطلبات');
    } finally {
      setLoading(false);
    }
  }, [guestId]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  // Group requests by bookingId, decorate each group with its booking summary
  // (looked up from guest.bookings — same shape as BookingDetails passes to the
  // modal, so the modal context block renders identically).
  const groupedByBooking = useMemo(() => {
    const bookingMap = new Map(guestBookings.map((b) => [String(b._id), b]));
    const groups = new Map();

    for (const req of requests) {
      // Backend populates `bookingId` to the full booking doc (see POPULATE_FIELDS
      // in guestRequestRoutes.js). Read `_id` first, fall back to the raw value
      // for safety if populate is ever skipped or shape changes.
      const rawBookingId = req.bookingId?._id || req.bookingId || req.booking?._id || '';
      const bookingIdKey = String(rawBookingId);
      if (!groups.has(bookingIdKey)) {
        groups.set(bookingIdKey, {
          bookingId: bookingIdKey,
          booking: bookingMap.get(bookingIdKey) || null,
          requests: []
        });
      }
      groups.get(bookingIdKey).requests.push(req);
    }

    // Sort requests within each group: status → priority → createdAt desc
    groups.forEach((group) => {
      group.requests.sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 99;
        const sb = STATUS_ORDER[b.status] ?? 99;
        if (sa !== sb) return sa - sb;
        const pa = PRIORITY_ORDER[a.priority] ?? 99;
        const pb = PRIORITY_ORDER[b.priority] ?? 99;
        if (pa !== pb) return pa - pb;
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    });

    // Sort groups by booking checkIn desc — most recent stay first.
    // Groups whose booking lookup failed (orphan requests) sink to the bottom.
    return Array.from(groups.values()).sort((a, b) => {
      const da = a.booking?.checkIn ? new Date(a.booking.checkIn).getTime() : -Infinity;
      const db = b.booking?.checkIn ? new Date(b.booking.checkIn).getTime() : -Infinity;
      return db - da;
    });
  }, [requests, guestBookings]);

  const openModalFor = (booking) => {
    // Modal expects guestName resolvable on the booking — guest is the source of
    // truth on this page, so fold its name in for display.
    setSelectedBooking({ ...booking, guestName: guest?.name });
    setShowModal(true);
  };

  const handleCreateClick = () => {
    if (selectableBookings.length === 1) {
      openModalFor(selectableBookings[0]);
    }
    // 2+ bookings: handled by Dropdown items, not this handler
  };

  const handleCreated = (newRequest) => {
    setRequests((prev) => [newRequest, ...prev]);
    setShowModal(false);
    setSelectedBooking(null);
  };

  const handleHide = () => {
    setShowModal(false);
    setSelectedBooking(null);
  };

  const renderCreateButton = () => {
    if (selectableBookings.length === 0) {
      return (
        <Button variant="primary" disabled className="grs-new-btn touch-btn" title="لا توجد حجوزات صالحة">
          <i className="bi bi-plus-circle"></i> طلب جديد
        </Button>
      );
    }

    if (selectableBookings.length === 1) {
      return (
        <Button variant="primary" onClick={handleCreateClick} className="grs-new-btn touch-btn">
          <i className="bi bi-plus-circle"></i> طلب جديد
        </Button>
      );
    }

    return (
      <Dropdown align="end" dir="rtl">
        <Dropdown.Toggle variant="primary" className="grs-new-btn touch-btn" id="grh-new-request-toggle">
          <i className="bi bi-plus-circle"></i> طلب جديد
        </Dropdown.Toggle>
        <Dropdown.Menu className="grh-booking-menu">
          <Dropdown.Header>اختر الحجز للطلب الجديد</Dropdown.Header>
          {selectableBookings.map((b) => (
            <Dropdown.Item key={b._id} onClick={() => openModalFor(b)} className="grh-booking-menu-item">
              <div className="grh-booking-menu-property">
                {b.property?.name || 'عقار غير محدد'}
              </div>
              <div className="grh-booking-menu-dates">
                {formatDate(b.checkIn)} ← {formatDate(b.checkOut)}
              </div>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  return (
    <div className="info-card guest-requests-section guest-requests-history" dir="rtl">
      <div className="grs-header">
        <h3 className="grs-title">
          <i className="bi bi-clipboard-check grs-title-icon"></i>
          سجل الطلبات
          {!loading && requests.length > 0 && (
            <span className="grs-count">{requests.length}</span>
          )}
        </h3>
        {!isTurnKeyOwner && renderCreateButton()}
      </div>

      {loading && (
        <div className="grs-loading">
          <Spinner animation="border" size="sm" />
          <span>جاري تحميل الطلبات...</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="danger" className="grs-error">{error}</Alert>
      )}

      {!loading && !error && groupedByBooking.length === 0 && (
        <div className="grs-empty">
          <i className="bi bi-inbox grs-empty-icon"></i>
          <p>لا توجد طلبات سابقة</p>
          {selectableBookings.length > 0 && !isTurnKeyOwner && (
            <p className="grs-empty-hint">
              اضغط على "طلب جديد" لإضافة أول طلب لهذا الضيف
            </p>
          )}
        </div>
      )}

      {!loading && !error && groupedByBooking.length > 0 && (
        <div className="grh-groups">
          {groupedByBooking.map((group) => (
            <div key={group.bookingId} className="grh-group">
              <button
                type="button"
                className="grh-group-header"
                onClick={() => group.bookingId && navigate(`/bookings/${group.bookingId}`)}
                disabled={!group.bookingId}
                title={group.bookingId ? 'عرض الحجز' : ''}
              >
                <div className="grh-group-header__main">
                  <i className="bi bi-house-door grh-group-header__icon"></i>
                  <span className="grh-group-header__property">
                    {group.booking?.property?.name || 'حجز محذوف'}
                  </span>
                  {group.booking && (
                    <span className="grh-group-header__dates">
                      {formatDate(group.booking.checkIn)} ← {formatDate(group.booking.checkOut)}
                    </span>
                  )}
                </div>
                <span className="grh-group-header__count">
                  {group.requests.length} {group.requests.length === 1 ? 'طلب' : 'طلبات'}
                  <i className="bi bi-chevron-left grh-group-header__chevron"></i>
                </span>
              </button>

              <div className="grs-list grh-group-list">
                {group.requests.map((req) => (
                  <div key={req._id} className={`grs-row grs-row--${req.status}`}>
                    <div className="grs-row__main">
                      <div className="grs-row__head">
                        <span className={`grs-type grs-type--${req.type}`}>
                          {TYPE_LABELS[req.type] || req.type}
                        </span>
                        <span className={`grs-priority grs-priority--${req.priority}`}>
                          {PRIORITY_LABELS[req.priority] || req.priority}
                        </span>
                        {!isTurnKeyOwner && req.fulfillmentType === 'external' && (
                          <span className="grs-fulfillment grs-fulfillment--external">
                            تنفيذ خارجي
                          </span>
                        )}
                        <span className="grs-timestamp">{formatRelativeTime(req.createdAt)}</span>
                      </div>

                      {req.description && (
                        <p className="grs-description">{req.description}</p>
                      )}

                      <div className="grs-row__meta">
                        {req.requestedForDate && (
                          <span className="grs-requested-for" title="موعد التنفيذ المطلوب من الضيف">
                            <i className="bi bi-calendar-event"></i> موعد التنفيذ المطلوب: {formatDate(req.requestedForDate)}
                          </span>
                        )}
                        {!isTurnKeyOwner && req.assignedTo && (
                          <span className="grs-assigned">
                            <i className="bi bi-person"></i> {req.assignedTo}
                          </span>
                        )}
                        {!isTurnKeyOwner && req.linkedTaskTicketId && (
                          <span className="grs-task-link">
                            <i className="bi bi-link-45deg"></i>
                            مهمة #{req.linkedTaskTicketId}
                            {req.linkedTaskStatus && (
                              <span className={`grs-task-status grs-task-status--${req.linkedTaskStatus}`}>
                                {STATUS_LABELS[req.linkedTaskStatus] || req.linkedTaskStatus}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Read-only here. Status mutation lives on BookingDetails — the
                        group-header click sends ops there. Keeps a single edit surface
                        and avoids duplicating optimistic-update logic. */}
                    <div className="grs-row__status">
                      <span
                        className={`grs-status-badge grs-status-badge--${req.status}`}
                        title={isTurnKeyOwner ? '' : 'افتح صفحة الحجز لتعديل الحالة'}
                      >
                        {statusLabels[req.status] || req.status}
                        {!isTurnKeyOwner && <i className="bi bi-lock-fill grs-status-locked"></i>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <GuestRequestModal
        show={showModal}
        onHide={handleHide}
        booking={selectedBooking}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default GuestRequestsHistory;
